import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import type {ReactNode} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {InlineStat} from 'src/design/molecules/InlineStat';

export interface AssistantUsageStat {
  label: string;
  value: ReactNode;
}

export interface AssistantUsageSectionProps {
  title?: string;
  stats: AssistantUsageStat[];
  isLoading: boolean;
  isError: boolean;
  /** Rendered instead of the stats and children when set. */
  empty?: ReactNode;
  /** Detail rendered below the stat row (chart, table). */
  children?: ReactNode;
}

/**
 * Control Center stat section: a titled card with an InlineStat row over a detail view.
 * @registry Scout Control Center stat section (loading/error/empty, InlineStat row, detail below).
 */
export const AssistantUsageSection = ({
  title = 'Usage',
  stats,
  isLoading,
  isError,
  empty,
  children,
}: AssistantUsageSectionProps) => (
  <SectionCard title={title}>
    <Stack gap="md" p="md">
      {isLoading ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Alert color="red" title={`Unable to load ${title.toLowerCase()}`}>
          The {title.toLowerCase()} overview could not be loaded. Please try
          again.
        </Alert>
      ) : empty ? (
        empty
      ) : (
        <>
          <SimpleGrid cols={{base: 2, sm: 3, md: stats.length}} spacing="md">
            {stats.map(stat => (
              <InlineStat key={stat.label} label={stat.label} value={stat.value} />
            ))}
          </SimpleGrid>
          <Divider />
          {children}
        </>
      )}
    </Stack>
  </SectionCard>
);
